// FilterSelector.tsx
import React, { useState, useEffect, useMemo, useCallback, useImperativeHandle, forwardRef } from 'react';
import './ViewFilterSelectorStyleSheet.css';
import { DiaryItem, DiaryItemType, DiaryCreatedSource } from "../../models/diaryTypes";

export type FilterItem = {
    id: string;
    label: string;
    icon: string;
    color: string;
    description: string;
    checked: boolean;
    shortLabel?: string;
    filterFn: (item: DiaryItem) => boolean;
    category: 'type' | 'attribute';
    dependencies?: string[];
};

export const filterFunctions = {
    // Primary type filters - only show parent items (no parentId)
    tasks: (item: DiaryItem) =>
        item.type === DiaryItemType.Task && (item.parentDiaryItemId === null),

    events: (item: DiaryItem) =>
        item.type === DiaryItemType.Event && (item.parentDiaryItemId === null),

    // Attribute filters - show linked items based on created source
    series: (item: DiaryItem) =>
        (item.createdSource === DiaryCreatedSource.System) && (!!item.parentDiaryItemId),

    linked: (item: DiaryItem) =>
        (item.createdSource === DiaryCreatedSource.User) && (!!item.parentDiaryItemId),

    // Combined filters for display and additive logic
    tasksWithSeries: (item: DiaryItem) =>
        item.type === DiaryItemType.Task &&
        (item.parentDiaryItemId === null ||
            (item.createdSource === DiaryCreatedSource.System && !!item.parentDiaryItemId)),

    tasksWithLinked: (item: DiaryItem) =>
        item.type === DiaryItemType.Task &&
        (item.parentDiaryItemId === null ||
            (item.createdSource === DiaryCreatedSource.User && !!item.parentDiaryItemId)),

    eventsWithSeries: (item: DiaryItem) =>
        item.type === DiaryItemType.Event &&
        (item.parentDiaryItemId === null ||
            (item.createdSource === DiaryCreatedSource.System && !!item.parentDiaryItemId)),

    eventsWithLinked: (item: DiaryItem) =>
        item.type === DiaryItemType.Event &&
        (item.parentDiaryItemId === null ||
            (item.createdSource === DiaryCreatedSource.User && !!item.parentDiaryItemId)),

    // For display counts only
    seriesTasks: (item: DiaryItem) =>
        item.type === DiaryItemType.Task &&
        (item.createdSource === DiaryCreatedSource.System) &&
        (!!item.parentDiaryItemId),

    seriesEvents: (item: DiaryItem) =>
        item.type === DiaryItemType.Event &&
        (item.createdSource === DiaryCreatedSource.System) &&
        (!!item.parentDiaryItemId),

    linkedTasks: (item: DiaryItem) =>
        item.type === DiaryItemType.Task &&
        (item.createdSource === DiaryCreatedSource.User) &&
        (!!item.parentDiaryItemId),

    linkedEvents: (item: DiaryItem) =>
        item.type === DiaryItemType.Event &&
        (item.createdSource === DiaryCreatedSource.User) &&
        (!!item.parentDiaryItemId)
};

export type ViewFilterMode = 'checkbox' | 'switch';
export type LayoutMode = 'horizontal' | 'vertical' | 'auto';

export const defaultViewFilters: FilterItem[] = [
    {
        id: 'tasks',
        label: 'Tasks',
        shortLabel: 'Tasks',
        icon: '✓',
        color: '#4CAF50',
        description: 'Individual to-do items and action items',
        checked: true,
        filterFn: filterFunctions.tasks,
        category: 'type'
    },
    {
        id: 'events',
        label: 'Events',
        shortLabel: 'Events',
        icon: '📅',
        color: '#2196F3',
        description: 'Calendar events and appointments',
        checked: true,
        filterFn: filterFunctions.events,
        category: 'type'
    },
    {
        id: 'series',
        label: 'Series',
        shortLabel: 'Series',
        icon: '🔄',
        color: '#9C27B0',
        description: 'Recurring items and sequences',
        checked: true,
        filterFn: filterFunctions.series,
        category: 'attribute',
        dependencies: ['tasks', 'events']
    },
    {
        id: 'linked-items',
        label: 'Linked Items',
        shortLabel: 'Linked',
        icon: '🔗',
        color: '#FF9800',
        description: 'Items connected to other content',
        checked: true,
        filterFn: filterFunctions.linked,
        category: 'attribute',
        dependencies: ['tasks', 'events']
    }
];

export interface FilterSelectorHandle {
    toggleFilter: (filterId: string) => void;
    setFilter: (filterId: string, checked: boolean) => void;
    setAllFilters: (checked: boolean, category?: 'type' | 'attribute') => void;
    getActiveFilters: () => string[];
    getFilterState: () => FilterItem[];
    resetFilters: () => void;
}
interface FilterSelectorProps {
    initialFilters?: Partial<FilterItem>[];
    onFilterChange?: (filters: FilterItem[]) => void;
    defaultViewMode?: ViewFilterMode;
    layoutMode?: LayoutMode;
    compact?: boolean;
}

const ViewFilterSelector = forwardRef<FilterSelectorHandle, FilterSelectorProps>(({
    initialFilters,
    onFilterChange,
    defaultViewMode = 'checkbox',
    layoutMode = 'auto',
    compact = false
}, ref) => {
    const [viewMode, setViewMode] = useState<ViewFilterMode>(defaultViewMode);
    const [showAll, setShowAll] = useState(false);
    const [isWideScreen, setIsWideScreen] = useState(window.innerWidth >= 1024);

    const [filters, setFilters] = useState<FilterItem[]>(
        initialFilters
            ? defaultViewFilters.map(df => {
                const customFilter = initialFilters.find(f => f.id === df.id);
                return customFilter ? { ...df, ...customFilter } : df;
            })
            : defaultViewFilters
    );

    useEffect(() => {
        onFilterChange?.(filters);
    }, [filters]);

    useEffect(() => {
        const handleResize = () => {
            setIsWideScreen(window.innerWidth >= 1024);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleFilterToggle = (id: string) => {
        setFilters(prev => prev.map(filter =>
            filter.id === id ? { ...filter, checked: !filter.checked } : filter
        ));
    };

    const handleSelectAll = (select: boolean) => {
        setFilters(prev => prev.map(filter => ({ ...filter, checked: select })));
    };

    const handleViewModeToggle = () => {
        setViewMode(prev => prev === 'checkbox' ? 'switch' : 'checkbox');
    };

    const handleLayoutToggle = () => {
        setShowAll(prev => !prev);
    };

    const selectedCount = filters.filter(f => f.checked).length;
    const totalCount = filters.length;

    // Determine layout based on props and screen size
    const effectiveLayout = layoutMode === 'auto'
        ? (isWideScreen ? 'horizontal' : 'vertical')
        : layoutMode;

    const isHorizontal = effectiveLayout === 'horizontal';

    // Internal set filter handler
    const handleSetFilter = useCallback((filterId: string, checked: boolean) => {
        setFilters(prev => prev.map(filter =>
            filter.id === filterId ? { ...filter, checked: checked } : filter
        ));
    }, []);

    // Internal reset handler
    const handleResetFilters = useCallback(() => {
        setFilters(prev => prev.map(filter => ({
            ...filter,
            checked: filter.category === 'type' ? true : false
        })));
    }, []);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
        toggleFilter: (filterId: string) => {
            handleFilterToggle(filterId);
        },
        setFilter: (filterId: string, checked: boolean) => {
            handleSetFilter(filterId, checked);
        },
        setAllFilters: (checked: boolean, category?: 'type' | 'attribute') => {
            handleSelectAll(checked);
        },
        getActiveFilters: () => {
            return filters.filter(f => f.checked).map(f => f.id);
        },
        getFilterState: () => {
            return [...filters];
        },
        resetFilters: () => {
            handleResetFilters();
        }
    }), [handleFilterToggle, handleSetFilter, handleSelectAll, handleResetFilters, filters]);

    return (
        <div className={`filter-selector ${compact ? 'compact' : ''} ${isHorizontal ? 'horizontal-layout' : 'vertical-layout'}`}>
            <div className="filter-header">
                <div className="filter-title">
                    <h3>View Filters</h3>
                    <p className="filter-subtitle">
                        {isHorizontal ? (
                            <span className="filter-count">
                                {selectedCount}/{totalCount} active
                            </span>
                        ) : (
                            `Showing ${selectedCount} of ${totalCount} items types`
                        )}
                    </p>
                </div>
            </div>

            {!isHorizontal && (
                <div className="bulk-actions">
                    <button
                        className="bulk-action-btn"
                        onClick={() => handleSelectAll(true)}
                    >
                        Select All
                    </button>
                    <button
                        className="bulk-action-btn"
                        onClick={() => handleSelectAll(false)}
                    >
                        Deselect All
                    </button>
                </div>
            )}

            <div className={`filter-list ${isHorizontal ? 'horizontal' : 'vertical'}`}>
                {filters.map(filter => (
                    <div
                        key={filter.id}
                        className={`filter-item ${filter.checked ? 'active' : ''} ${isHorizontal ? 'horizontal-item' : ''}`}
                        onClick={() => handleFilterToggle(filter.id)}
                    >
                        <div className={`filter-item-main ${isHorizontal ? 'horizontal-main' : ''}`}>
                            <div
                                className="filter-icon"
                                style={{ backgroundColor: filter.color }}
                            >
                                {filter.icon}
                            </div>

                            <div className="filter-content">
                                <h4>{isHorizontal && !showAll ? filter.shortLabel || filter.label : filter.label}</h4>
                                {(showAll) && !compact && (
                                    <p className="filter-description">{filter.description}</p>
                                )}
                            </div>

                            <div className="filter-toggle">
                                {viewMode === 'checkbox' ? (
                                    <div className={`checkbox ${filter.checked ? 'checked' : ''}`}>
                                        {filter.checked && '✓'}
                                    </div>
                                ) : (
                                    <div className="switch">
                                        <div className={`switch-slider ${filter.checked ? 'active' : ''}`}>
                                            <div className="switch-handle" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {showAll && (
                            <div className="horizontal-description">
                                {filter.description}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {isHorizontal && (
                <div className="horizontal-controls">
                    <div className="horizontal-actions">
                        <button
                            className="bulk-action-btn"
                            onClick={() => handleSelectAll(true)}
                        >
                            Select All
                        </button>
                        <button
                            className="bulk-action-btn"
                            onClick={() => handleSelectAll(false)}
                        >
                            Deselect All
                        </button>
                    </div>

                </div>
            )}

            {compact && selectedCount < totalCount && (
                <div className="compact-summary">
                    <span>{selectedCount} filters active</span>
                    <button
                        className="clear-filters"
                        onClick={() => handleSelectAll(false)}
                    >
                        Clear all
                    </button>
                </div>
            )}
        </div>
    );
});

ViewFilterSelector.displayName = 'FilterSelector';

export default ViewFilterSelector;